import React, { useState, useEffect, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster } from "./components/ui/sonner";

// Contexts
import { AuthProvider, useAuth } from "./context/AuthContext";

// Layout Components
import Layout from "./components/layout/Layout";
import AdminLayout from "./components/layout/AdminLayout";
import MemberPortalLayout from "./components/layout/MemberPortalLayout";

// Public Pages
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import MembershipPage from "./pages/MembershipPage";
import EventsPage from "./pages/EventsPage";
import EventDetailPage from "./pages/EventDetailPage";
import PublicPostPage from "./pages/PublicPostPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import MobileAppLayout from "./components/layout/MobileAppLayout";
import { AppFeedPage, AppMessagesPage, AppDealsPage, AppProfilePage } from "./components/AppPages";
import AppNotificationsPage from "./pages/app/AppNotificationsPage";
import AppAdminPage from "./pages/app/AppAdminPage";
import AppOfflinePage from "./pages/app/AppOfflinePage";
import LeadershipPage from "./pages/LeadershipPage";
import MembersDirectoryPage from "./pages/MembersDirectoryPage";
import BrandResourcesPage from "./pages/BrandResourcesPage";
import MarketingRequestPage from "./pages/MarketingRequestPage";
import ContactPage from "./pages/ContactPage";
import NewsPage from "./pages/NewsPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import FAQPage from "./pages/FAQPage";
import BusinessProfilePage from "./pages/BusinessProfilePage";
import LeaderProfilePage from "./pages/LeaderProfilePage";

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import MemberLoginPage from "./pages/auth/MemberLoginPage";
import InviteActivationPage from "./pages/auth/InviteActivationPage";
import AuthCallback from "./pages/auth/AuthCallback";
import ActivatePage from "./pages/auth/ActivatePage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";

// Admin Pages
import DashboardPage from "./pages/admin/DashboardPage";
import EventsManagementPage from "./pages/admin/EventsManagementPage";
import MembersManagementPage from "./pages/admin/MembersManagementPage";
import MemberActivationAuditPage from "./pages/admin/MemberActivationAuditPage";
import MemberCenterPage from "./pages/admin/MemberCenterPage";
import NewsletterCenterPage from "./pages/admin/NewsletterCenterPage";
import WelcomeExperienceManager from "./pages/admin/WelcomeExperienceManager";
import AdminPresencePage from "./pages/admin/AdminPresencePage";
import AdminInvitationsPage from "./pages/admin/AdminInvitationsPage";
import Welcome from "./pages/Welcome";
import NotificationsPage from "./pages/NotificationsPage";
import BlogManagementPage from "./pages/admin/BlogManagementPage";
import MarketingRequestsPage from "./pages/admin/MarketingRequestsPage";
import BrandRequestsPage from "./pages/admin/BrandRequestsPage";
import UsersManagementPage from "./pages/admin/UsersManagementPage";
import AnnouncementsPage from "./pages/admin/AnnouncementsPage";
import BlogReviewPage from "./pages/admin/BlogReviewPage";
import SupportInboxPage from "./pages/admin/SupportInboxPage";
import SocialRequestsReviewPage from "./pages/admin/SocialRequestsReviewPage";
import CommitteeApplicationsPage from "./pages/admin/CommitteeApplicationsPage";
import MembershipPlansPage from "./pages/admin/MembershipPlansPage";
import MemberDealsAccessPage from "./pages/admin/MemberDealsAccessPage";
import AdminMemberDealsPage from "./pages/admin/AdminMemberDealsPage";
import AdminHomepageManager from "./pages/admin/AdminHomepageManager";
import AdminNewsletterCenter from "./pages/admin/AdminNewsletterCenter";
import AdminExternalNews from "./pages/admin/AdminExternalNews";
import EmailCommunicationsPage from "./pages/admin/EmailCommunicationsPage";
import AIAgentPage from "./pages/admin/AIAgentPage";
import AIAgentActivityPage from "./pages/admin/AIAgentActivityPage";
import MediaLibraryPage from "./pages/admin/MediaLibraryPage";
import CommunicationCenterPage from "./pages/admin/CommunicationCenterPage";
import AdminTestimonialsPage from "./pages/admin/AdminTestimonialsPage";
import AdminNewsAdsPage from "./pages/admin/AdminNewsAdsPage";
import CommunityModerationPage from "./pages/admin/CommunityModerationPage";
import MembershipApplicationsPage from "./pages/admin/MembershipApplicationsPage";
import LeadershipManagementPage from "./pages/admin/LeadershipManagementPage";
import BoardDashboardPage from "./pages/admin/BoardDashboardPage";

// Portal Pages
import PortalDashboard from "./pages/portal/PortalDashboard";
import PortalBenefits from "./pages/portal/PortalBenefits";
import EmailPreferencesPage from "./pages/portal/EmailPreferencesPage";
import AccountSettingsPage from "./pages/portal/AccountSettingsPage";
import PortalMemberDeals from "./pages/portal/PortalMemberDeals";
import PortalEvents from "./pages/portal/PortalEvents";
import PortalCommittees from "./pages/portal/PortalCommittees";
import PortalRequestCenter from "./pages/portal/PortalRequestCenter";
import PortalBlogSubmission from "./pages/portal/PortalBlogSubmission";
import PortalSocialRequests from "./pages/portal/PortalSocialRequests";
import PortalCompanyProfile from "./pages/portal/PortalCompanyProfile";
import PortalCommunity from "./pages/portal/PortalCommunity";
import PortalSupport from "./pages/portal/PortalSupport";
import MemberProfile from "./pages/portal/MemberProfile";
import BusinessProfile from "./pages/portal/BusinessProfile";
import PortalMessages from "./pages/portal/PortalMessages";
import PortalSocialProfile from "./pages/portal/PortalSocialProfile";
import PortalMembersDirectory from "./pages/portal/PortalMembersDirectory";
import PortalBusinessDirectory from "./pages/portal/PortalBusinessDirectory";
import PortalLeadershipProfile from "./pages/portal/PortalLeadershipProfile";

// Loading Screen Component
const LoadingScreen = ({ onComplete }) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(onComplete, 800);
    }, 3200);

    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      className="loading-screen"
      initial={{ opacity: 1 }}
      animate={{ opacity: isExiting ? 0 : 1 }}
      transition={{ duration: 0.8 }}
    >
      <div className="flex flex-col items-center">
        <motion.img
          src="/brand/logo-white.png"
          alt="DCCF Logo"
          className="w-48 md:w-64 lg:w-80"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
        <motion.div
          className="mt-8 flex space-x-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="w-2 h-2 bg-white rounded-full"
              animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
            />
          ))}
        </motion.div>
        <motion.p
          className="text-white/80 mt-6 text-sm tracking-widest uppercase"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.5 }}
        >
          Connecting Communities
        </motion.p>
      </div>
    </motion.div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/member-login" state={{ from: location }} replace />;
  }

  // First-login onboarding redirect — show once, then never again
  if (user.is_first_login && !location.pathname.startsWith('/welcome')) {
    return <Navigate to="/welcome" replace />;
  }

  // Super admin always passes
  if (user.role === 'super_admin') return children;

  // Check if user's role is in the allowed list
  if (allowedRoles && allowedRoles.includes(user.role)) {
    return children;
  }

  // Users with admin_access can enter admin routes (except super_admin-only routes)
  if (user.admin_access && allowedRoles && !allowedRoles.every(r => r === 'super_admin')) {
    return children;
  }

  // Users with ai_agent_access can enter the admin shell (AdminLayout filters
  // the sidebar; ModuleGuard still blocks any admin route they shouldn't see).
  if (user.ai_agent_access && allowedRoles && !allowedRoles.every(r => r === 'super_admin')) {
    return children;
  }

  // Unauthorized — redirect to portal (not admin)
  return <Navigate to="/portal" replace />;
};

// Module Permission Guard — blocks route access when module is OFF
const ModuleGuard = ({ module, children }) => {
  const { user } = useAuth();
  // ProtectedRoute already handles auth loading — just check permissions
  if (!user) return children;
  
  // Check if this is a portal module (portal_* prefix) vs admin module
  const isPortalModule = module?.startsWith('portal_');
  
  // Leadership users with admin_access check admin_modules ONLY for admin routes
  // Portal routes should use standard module_permissions check
  if (user.admin_access && user.leadership_level && user.role === 'member' && !isPortalModule) {
    const adminMods = user.admin_modules || {};
    if (module in adminMods && adminMods[module] === false) {
      return <Navigate to="/admin" replace />;
    }
    // If module not in admin_modules at all, block access (deny by default for leadership)
    if (!(module in adminMods) || adminMods[module] !== true) {
      return <Navigate to="/admin" replace />;
    }
    return children;
  }
  
  // Super admin always passes
  if (user.role === 'super_admin') return children;
  
  const perms = user.module_permissions;
  if (!perms || typeof perms !== 'object') return children;
  if (module in perms && perms[module] === false) {
    return <Navigate to={user.role === 'member' ? '/portal' : '/admin'} replace />;
  }
  return children;
};

// Global scroll-to-top on route change (ignores hash anchors)
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

// App Router Component
const AppRouter = () => {
  const location = useLocation();

  // Handle OAuth callback - check synchronously before render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="membership" element={<MembershipPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="events/:id" element={<EventDetailPage />} />
        <Route path="leadership" element={<LeadershipPage />} />
        <Route path="leadership/:leaderId" element={<LeaderProfilePage />} />
        <Route path="members" element={<MembersDirectoryPage />} />
        <Route path="business/:businessId" element={<BusinessProfilePage />} />
        <Route path="brand-resources" element={<BrandResourcesPage />} />
        <Route path="marketing-request" element={<MarketingRequestPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="news" element={<NewsPage />} />
        <Route path="news/:postId" element={<BlogDetailPage />} />
        <Route path="blog/:postId" element={<BlogDetailPage />} />
        <Route path="privacy" element={<PrivacyPolicyPage />} />
        <Route path="terms" element={<TermsOfServicePage />} />
        <Route path="faq" element={<FAQPage />} />
      </Route>

      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/member-login" element={<MemberLoginPage />} />
      <Route path="/invite/:token" element={<InviteActivationPage />} />
      <Route path="/activate" element={<ActivatePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/post/:id" element={<PublicPostPage />} />
      <Route path="/community" element={<Navigate to="/portal" replace />} />

      {/* Mobile App (PWA) — same backend, same auth, same data */}
      <Route path="/app" element={<MobileAppLayout />}>
        <Route index element={<Navigate to="/app/feed" replace />} />
        <Route path="feed" element={<AppFeedPage />} />
        <Route path="community" element={<Navigate to="/app/feed" replace />} />
        <Route path="messages" element={<AppMessagesPage />} />
        <Route path="messages/:userId" element={<AppMessagesPage />} />
        <Route path="notifications" element={<AppNotificationsPage />} />
        <Route path="deals" element={<AppDealsPage />} />
        <Route path="profile" element={<AppProfilePage />} />
        <Route path="admin" element={<AppAdminPage />} />
        <Route path="offline" element={<AppOfflinePage />} />
      </Route>
      <Route
        path="/welcome"
        element={
          <ProtectedRoute allowedRoles={['member', 'super_admin', 'membership_manager', 'membership_lead', 'membership_support', 'marketing_team', 'events_manager', 'committee_manager', 'blog_reviewer']}>
            <Welcome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'membership_manager', 'membership_lead', 'membership_support', 'marketing_team', 'events_manager', 'committee_manager', 'blog_reviewer']}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ModuleGuard module="dashboard"><DashboardPage /></ModuleGuard>} />
        <Route path="events" element={<ModuleGuard module="events"><EventsManagementPage /></ModuleGuard>} />
        <Route path="members" element={<ModuleGuard module="members"><MembersManagementPage /></ModuleGuard>} />
        <Route path="activation-audit" element={<ModuleGuard module="members"><MemberActivationAuditPage /></ModuleGuard>} />
        <Route path="member-center" element={<ModuleGuard module="member_center"><MemberCenterPage /></ModuleGuard>} />
        <Route path="newsletter" element={<ModuleGuard module="newsletter_center"><NewsletterCenterPage /></ModuleGuard>} />
        <Route path="welcome-experience" element={<ModuleGuard module="platform_settings"><WelcomeExperienceManager /></ModuleGuard>} />
        <Route path="invitations" element={<ModuleGuard module="newsletter_center"><AdminInvitationsPage /></ModuleGuard>} />
        <Route path="presence" element={<ModuleGuard module="user_management"><AdminPresencePage /></ModuleGuard>} />
        <Route path="blog" element={<ModuleGuard module="blog_news"><BlogManagementPage /></ModuleGuard>} />
        <Route path="marketing-requests" element={<ModuleGuard module="marketing_requests"><MarketingRequestsPage /></ModuleGuard>} />
        <Route path="brand-requests" element={<ModuleGuard module="brand_requests"><BrandRequestsPage /></ModuleGuard>} />
        <Route path="announcements" element={<ModuleGuard module="announcements"><AnnouncementsPage /></ModuleGuard>} />
        <Route path="blog-review" element={<ModuleGuard module="blog_review"><BlogReviewPage /></ModuleGuard>} />
        <Route path="social-review" element={<ModuleGuard module="social_requests"><SocialRequestsReviewPage /></ModuleGuard>} />
        <Route path="committee-apps" element={<ModuleGuard module="committees"><CommitteeApplicationsPage /></ModuleGuard>} />
        <Route path="support" element={<ModuleGuard module="support_inbox"><SupportInboxPage /></ModuleGuard>} />
        <Route path="membership-plans" element={<ModuleGuard module="membership_plans"><MembershipPlansPage /></ModuleGuard>} />
        <Route path="member-deals-access" element={<ModuleGuard module="membership_plans"><MemberDealsAccessPage /></ModuleGuard>} />
        <Route path="member-deals" element={<ModuleGuard module="membership_plans"><AdminMemberDealsPage /></ModuleGuard>} />
        <Route path="homepage" element={<ProtectedRoute allowedRoles={['super_admin', 'marketing_team']}><AdminHomepageManager /></ProtectedRoute>} />
        <Route path="newsletter-center" element={<ProtectedRoute allowedRoles={['super_admin', 'events_manager', 'membership_lead', 'membership_support', 'marketing_team', 'blog_reviewer']}><AdminNewsletterCenter /></ProtectedRoute>} />
        <Route path="external-news" element={<ProtectedRoute allowedRoles={['super_admin', 'marketing_team']}><AdminExternalNews /></ProtectedRoute>} />
        <Route path="email-communications" element={<ModuleGuard module="email_communications"><EmailCommunicationsPage /></ModuleGuard>} />
        <Route path="ai-agent" element={<AIAgentPage />} />
        <Route path="ai-agent/activity" element={<ProtectedRoute allowedRoles={['super_admin']}><AIAgentActivityPage /></ProtectedRoute>} />
        <Route path="media" element={<ProtectedRoute allowedRoles={['super_admin', 'marketing_team', 'events_manager', 'blog_reviewer', 'membership_lead', 'membership_support']}><MediaLibraryPage /></ProtectedRoute>} />
        <Route path="communication-center" element={<ProtectedRoute allowedRoles={['super_admin', 'marketing_team', 'membership_lead', 'membership_support']}><CommunicationCenterPage /></ProtectedRoute>} />
        <Route path="communication" element={<ProtectedRoute allowedRoles={['super_admin', 'marketing_team', 'membership_lead', 'membership_support']}><CommunicationCenterPage /></ProtectedRoute>} />
        <Route path="testimonials" element={<ProtectedRoute allowedRoles={['super_admin', 'marketing_team', 'membership_lead']}><AdminTestimonialsPage /></ProtectedRoute>} />
        <Route path="news-ads" element={<AdminNewsAdsPage />} />
        <Route path="community" element={<ModuleGuard module="community_moderation"><CommunityModerationPage /></ModuleGuard>} />
        <Route path="applications" element={<ModuleGuard module="applications"><MembershipApplicationsPage /></ModuleGuard>} />
        <Route
          path="leadership"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <LeadershipManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="users"
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <UsersManagementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="board"
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'membership_lead']}>
              <BoardDashboardPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Member Portal Routes */}
      <Route
        path="/portal"
        element={
          <ProtectedRoute allowedRoles={['member', 'super_admin', 'membership_lead', 'membership_support', 'marketing_team', 'events_manager', 'committee_manager', 'blog_reviewer']}>
            <MemberPortalLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ModuleGuard module="portal_community"><PortalCommunity /></ModuleGuard>} />
        <Route path="dashboard" element={<PortalDashboard />} />
        <Route path="benefits" element={<ModuleGuard module="portal_benefits"><PortalBenefits /></ModuleGuard>} />
        <Route path="events" element={<ModuleGuard module="portal_events"><PortalEvents /></ModuleGuard>} />
        <Route path="committees" element={<ModuleGuard module="portal_committees"><PortalCommittees /></ModuleGuard>} />
        <Route path="requests" element={<ModuleGuard module="portal_requests"><PortalRequestCenter /></ModuleGuard>} />
        <Route path="blog" element={<ModuleGuard module="portal_blog"><PortalBlogSubmission /></ModuleGuard>} />
        <Route path="social" element={<ModuleGuard module="portal_social"><PortalSocialRequests /></ModuleGuard>} />
        <Route path="profile" element={<ModuleGuard module="portal_profile"><PortalCompanyProfile /></ModuleGuard>} />
        <Route path="email-preferences" element={<EmailPreferencesPage />} />
        <Route path="account" element={<AccountSettingsPage />} />
        <Route path="social-profile" element={<PortalSocialProfile />} />
        <Route path="community" element={<Navigate to="/portal" replace />} />
        <Route path="members/:memberId" element={<MemberProfile />} />
        <Route path="business/:userId" element={<BusinessProfile />} />
        <Route path="member-directory" element={<PortalMembersDirectory />} />
        <Route path="business-directory" element={<PortalBusinessDirectory />} />
        <Route path="member-deals" element={<PortalMemberDeals />} />
        <Route path="my-leadership" element={<PortalLeadershipProfile />} />
        <Route path="messages" element={<PortalMessages />} />
        <Route path="support" element={<ModuleGuard module="portal_support"><PortalSupport /></ModuleGuard>} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  const [showLoading, setShowLoading] = useState(true);

  const handleLoadingComplete = useCallback(() => {
    setShowLoading(false);
  }, []);

  return (
    <>
      <AnimatePresence>
        {showLoading && <LoadingScreen onComplete={handleLoadingComplete} />}
      </AnimatePresence>
      
      {!showLoading && (
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <AppRouter />
            <Toaster position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      )}
    </>
  );
}

export default App;
